"use client";

import { useState } from "react";
import type { SquareVideoCardProps } from "./SquareVideoCard.types";
import VideoCardSkeleton from "../../skeletons/VideoCardSkeleton/VideoCardSkeleton";
import PrivateCdnImage from "@/asset-delivery/components/PrivateCdnImage";

import "./SquareVideoCard.css";

const SquareVideoCard = ({
  videoSrc, // kept for backward compatibility (unused in grid card)
  thumbnail,
  thumbnailTarget,
  thumbnailKind = "thumbnail",
  thumbnailFieldName = "thumbnail",
  episodeNumber,
  onClick,
  className = "",
}: SquareVideoCardProps) => {
  const [isLoaded, setIsLoaded] = useState(false);

  // normalize nullable props → undefined (React-friendly)
  const poster = thumbnail ?? undefined;

  return (
    <div
      className={`square-video-card ${className}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      {!isLoaded && <VideoCardSkeleton variant="square" />}

      {/* ✅ CDN-only thumbnail with cookie warmup */}
      {poster && thumbnailTarget ? (
        <PrivateCdnImage
          src={poster}
          placeholderSrc={null}
          alt="Video thumbnail"
          className={`square-video-element ${isLoaded ? "loaded" : "loading"}`}
          target={thumbnailTarget}
          kind={thumbnailKind as any}
          fieldName={thumbnailFieldName as any}
          rootMargin="700px"
          threshold={0.01}
          failOpen
          imgLoading="eager"
          fetchPriority="high"
          onLoad={() => setIsLoaded(true)}
          onError={() => setIsLoaded(true)} // prevent skeleton from hanging forever
        />
      ) : poster ? (
        // Optional local/plain fallback if target not provided
        <img
          src={poster}
          alt="Video thumbnail"
          className={`square-video-element ${isLoaded ? "loaded" : "loading"}`}
          loading="eager"
          decoding="async"
          draggable={false}
          onLoad={() => setIsLoaded(true)}
          onError={() => setIsLoaded(true)}
        />
      ) : (
        <div className={`square-video-element ${isLoaded ? "loaded" : "loading"}`} />
      )}

      {episodeNumber !== undefined && episodeNumber !== null && isLoaded && (
        <div className="square-episode-overlay">Episode {episodeNumber}</div>
      )}
    </div>
  );
};

export default SquareVideoCard;
